import { createSlice } from '@reduxjs/toolkit';
import { PaginationType } from '../../types/utils/pagination.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { RouteParamsType } from '../../types/utils/router-params.type';
import {
  MegaswitchInstanceType,
  MegaswitchInstanceListItemType,
  MegaswitchInstanceUserListItemType,
  CreateMegaswitchInstanceUserResponse,
} from '../../types/megaswitch/megaswitch-instance.type';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import snackbarUtils from '../../utils/SnackbarUtils';
import {
  getMegaswitchInstancesList,
  getMegaswitchInstanceDetails,
  postMegaswitchInstanceDetails,
  deleteMegaswitchInstanceDetails,
  getMegaswitchInstanceUsersList,
  postMegaswitchInstanceUserDetails,
  deleteMegaswitchInstanceUserDetails,
  getMegaswitchDomainsList,
  postMegaswitchDomainDetails,
  updateMegaswitchDomainDetails,
  deleteMegaswitchDomainDetails,
  getMegaswitchCarriersList,
  postMegaswitchCarrierDetails,
  updateMegaswitchCarrierDetails,
  deleteMegaswitchCarrierDetails,
  getMegaswitchDidsList,
  postMegaswitchDidDetails,
  updateMegaswitchDidDetails,
  deleteMegaswitchDidDetails,
  getMegaswitchUsersList,
  postMegaswitchUserDetails,
  updateMegaswitchUserDetails,
  deleteMegaswitchUserDetails,
  getMegaswitchOutboundRoutesList,
  postMegaswitchOutboundRouteDetails,
  updateMegaswitchOutboundRouteDetails,
  deleteMegaswitchOutboundRouteDetails,
  getMegaswitchCoresList,
  postMegaswitchCoreDetails,
  updateMegaswitchCoreDetails,
  deleteMegaswitchCoreDetails,
  patchMegaswitchInstanceDetails,
} from './megaswitch.action';
import { MegaswitchDomainType } from '../../types/megaswitch/meagswitch-domain.types';
import { MegaswitchCarrierType } from '../../types/megaswitch/megaswitch-carrier.types';
import { MegaswitchDidType } from '../../types/megaswitch/megaswitch-did.types';
import { MegaswitchUserType } from '../../types/megaswitch/megaswitch-user.types';
import { MegaswitchOutboundRouteType } from '../../types/megaswitch/megaswitch-outbound-route.types';
import { MegaswitchCoreType } from '../../types/megaswitch/megaswitch-core.types';

export interface MegaswitchState {
  megaswitchInstancesList: null | PaginationType<MegaswitchInstanceListItemType>;
  megaswitchInstancesListFetchStatus: FETCH_STATUS;
  megaswitchInstancesRouteParams: RouteParamsType;
  megaswitchInstanceDetails: null | MegaswitchInstanceType;
  megaswitchInstanceDetailsFetchStatus: FETCH_STATUS;
  megaswitchInstanceDetailsPostStatus: FETCH_STATUS;
  megaswitchInstanceDetailsPatchStatus: FETCH_STATUS;
  megaswitchInstanceDetailsDeleteStatus: FETCH_STATUS;

  megaswitchInstanceUsersList: null | PaginationType<MegaswitchInstanceUserListItemType>;
  megaswitchInstanceUsersListFetchStatus: FETCH_STATUS;
  megaswitchInstanceUsersRouteParams: RouteParamsType;
  megaswitchInstanceUserDetailsPostStatus: FETCH_STATUS;
  megaswitchInstanceUserDetailsDeleteStatus: FETCH_STATUS;
  newlyCreatedInstanceUser: null | (CreateMegaswitchInstanceUserResponse & { name: string; description: string });

  // Domains
  megaswitchDomainsList: null | PaginationType<MegaswitchDomainType>;
  megaswitchDomainsListFetchStatus: FETCH_STATUS;
  megaswitchDomainsRouteParams: RouteParamsType;
  megaswitchDomainDetailsPostStatus: FETCH_STATUS;
  megaswitchDomainDetailsUpdateStatus: FETCH_STATUS;
  megaswitchDomainDetailsDeleteStatus: FETCH_STATUS;

  // Carriers
  megaswitchCarriersList: null | PaginationType<MegaswitchCarrierType>;
  megaswitchCarriersListFetchStatus: FETCH_STATUS;
  megaswitchCarriersRouteParams: RouteParamsType;
  megaswitchCarrierDetailsPostStatus: FETCH_STATUS;
  megaswitchCarrierDetailsUpdateStatus: FETCH_STATUS;
  megaswitchCarrierDetailsDeleteStatus: FETCH_STATUS;

  // DIDs
  megaswitchDidsList: null | PaginationType<MegaswitchDidType>;
  megaswitchDidsListFetchStatus: FETCH_STATUS;
  megaswitchDidsRouteParams: RouteParamsType;
  megaswitchDidDetailsPostStatus: FETCH_STATUS;
  megaswitchDidDetailsUpdateStatus: FETCH_STATUS;
  megaswitchDidDetailsDeleteStatus: FETCH_STATUS;

  // Users
  megaswitchUsersList: null | PaginationType<MegaswitchUserType>;
  megaswitchUsersListFetchStatus: FETCH_STATUS;
  megaswitchUsersRouteParams: RouteParamsType;
  megaswitchUserDetailsPostStatus: FETCH_STATUS;
  megaswitchUserDetailsUpdateStatus: FETCH_STATUS;
  megaswitchUserDetailsDeleteStatus: FETCH_STATUS;

  // Outbound Routes
  megaswitchOutboundRoutesList: null | PaginationType<MegaswitchOutboundRouteType>;
  megaswitchOutboundRoutesListFetchStatus: FETCH_STATUS;
  megaswitchOutboundRoutesRouteParams: RouteParamsType;
  megaswitchOutboundRouteDetailsPostStatus: FETCH_STATUS;
  megaswitchOutboundRouteDetailsUpdateStatus: FETCH_STATUS;
  megaswitchOutboundRouteDetailsDeleteStatus: FETCH_STATUS;

  // Cores
  megaswitchCoresList: null | PaginationType<MegaswitchCoreType>;
  megaswitchCoresListFetchStatus: FETCH_STATUS;
  megaswitchCoresRouteParams: RouteParamsType;
  megaswitchCoreDetailsPostStatus: FETCH_STATUS;
  megaswitchCoreDetailsUpdateStatus: FETCH_STATUS;
  megaswitchCoreDetailsDeleteStatus: FETCH_STATUS;
}

export const initialMegaswitchInstancesRouteParams = {
  page: 1,
  limit: 25,
  order: '-id',
  q: '',
};

export const initialMegaswitchInstanceUsersRouteParams = {
  page: 1,
  limit: 25,
  order: '-id',
  q: '',
};

export const initialMegaswitchDomainsRouteParams = {
  page: 1,
  limit: 25,
  order: '-id',
  q: '',
};

export const initialMegaswitchCarriersRouteParams = {
  page: 1,
  limit: 25,
  order: '-id',
  q: '',
};

export const initialMegaswitchDidsRouteParams = {
  page: 1,
  limit: 25,
  order: '-daa.id',
  q: '',
};

export const initialMegaswitchUsersRouteParams = {
  page: 1,
  limit: 25,
  order: '-u.id',
  q: '',
};

export const initialMegaswitchOutboundRoutesRouteParams = {
  page: 1,
  limit: 25,
  order: '-id',
  q: '',
};

export const initialMegaswitchCoresRouteParams = {
  page: 1,
  limit: 25,
  order: '-id',
  q: '',
};

const INIT_STATE: MegaswitchState = {
  megaswitchInstancesRouteParams: initialMegaswitchInstancesRouteParams,
  megaswitchInstancesList: null,
  megaswitchInstancesListFetchStatus: FETCH_STATUS.NULL,
  megaswitchInstanceDetails: null,
  megaswitchInstanceDetailsFetchStatus: FETCH_STATUS.NULL,
  megaswitchInstanceDetailsPostStatus: FETCH_STATUS.NULL,
  megaswitchInstanceDetailsPatchStatus: FETCH_STATUS.NULL,
  megaswitchInstanceDetailsDeleteStatus: FETCH_STATUS.NULL,

  megaswitchInstanceUsersList: null,
  megaswitchInstanceUsersListFetchStatus: FETCH_STATUS.NULL,
  megaswitchInstanceUsersRouteParams: initialMegaswitchInstanceUsersRouteParams,
  megaswitchInstanceUserDetailsPostStatus: FETCH_STATUS.NULL,
  megaswitchInstanceUserDetailsDeleteStatus: FETCH_STATUS.NULL,
  newlyCreatedInstanceUser: null,

  // Domains
  megaswitchDomainsList: null,
  megaswitchDomainsListFetchStatus: FETCH_STATUS.NULL,
  megaswitchDomainsRouteParams: initialMegaswitchDomainsRouteParams,
  megaswitchDomainDetailsPostStatus: FETCH_STATUS.NULL,
  megaswitchDomainDetailsUpdateStatus: FETCH_STATUS.NULL,
  megaswitchDomainDetailsDeleteStatus: FETCH_STATUS.NULL,

  // Carriers
  megaswitchCarriersList: null,
  megaswitchCarriersListFetchStatus: FETCH_STATUS.NULL,
  megaswitchCarriersRouteParams: initialMegaswitchCarriersRouteParams,
  megaswitchCarrierDetailsPostStatus: FETCH_STATUS.NULL,
  megaswitchCarrierDetailsUpdateStatus: FETCH_STATUS.NULL,
  megaswitchCarrierDetailsDeleteStatus: FETCH_STATUS.NULL,

  // DIDs
  megaswitchDidsList: null,
  megaswitchDidsListFetchStatus: FETCH_STATUS.NULL,
  megaswitchDidsRouteParams: initialMegaswitchDidsRouteParams,
  megaswitchDidDetailsPostStatus: FETCH_STATUS.NULL,
  megaswitchDidDetailsUpdateStatus: FETCH_STATUS.NULL,
  megaswitchDidDetailsDeleteStatus: FETCH_STATUS.NULL,

  // Users
  megaswitchUsersList: null,
  megaswitchUsersListFetchStatus: FETCH_STATUS.NULL,
  megaswitchUsersRouteParams: initialMegaswitchUsersRouteParams,
  megaswitchUserDetailsPostStatus: FETCH_STATUS.NULL,
  megaswitchUserDetailsUpdateStatus: FETCH_STATUS.NULL,
  megaswitchUserDetailsDeleteStatus: FETCH_STATUS.NULL,

  // Outbound Routes
  megaswitchOutboundRoutesList: null,
  megaswitchOutboundRoutesListFetchStatus: FETCH_STATUS.NULL,
  megaswitchOutboundRoutesRouteParams: initialMegaswitchOutboundRoutesRouteParams,
  megaswitchOutboundRouteDetailsPostStatus: FETCH_STATUS.NULL,
  megaswitchOutboundRouteDetailsUpdateStatus: FETCH_STATUS.NULL,
  megaswitchOutboundRouteDetailsDeleteStatus: FETCH_STATUS.NULL,

  // Cores
  megaswitchCoresList: null,
  megaswitchCoresListFetchStatus: FETCH_STATUS.NULL,
  megaswitchCoresRouteParams: initialMegaswitchCoresRouteParams,
  megaswitchCoreDetailsPostStatus: FETCH_STATUS.NULL,
  megaswitchCoreDetailsUpdateStatus: FETCH_STATUS.NULL,
  megaswitchCoreDetailsDeleteStatus: FETCH_STATUS.NULL,
};

const megaswitchSlice = createSlice({
  name: 'megaswitch',
  initialState: INIT_STATE,
  reducers: {
    setMegaswitchInstancesList: (state, action) => {
      state.megaswitchInstancesList = action.payload;
    },
    setMegaswitchInstancesListFetchStatus: (state, action) => {
      state.megaswitchInstancesListFetchStatus = action.payload;
    },
    setMegaswitchInstanceDetails: (state, action) => {
      state.megaswitchInstanceDetails = action.payload;
    },
    setMegaswitchInstanceDetailsFetchStatus: (state, action) => {
      state.megaswitchInstanceDetailsFetchStatus = action.payload;
    },
    setMegaswitchInstanceDetailsPostStatus: (state, action) => {
      state.megaswitchInstanceDetailsPostStatus = action.payload;
    },
    setMegaswitchInstanceDetailsPatchStatus: (state, action) => {
      state.megaswitchInstanceDetailsPatchStatus = action.payload;
    },
    setMegaswitchInstanceDetailsDeleteStatus: (state, action) => {
      state.megaswitchInstanceDetailsDeleteStatus = action.payload;
    },
    setMegaswitchInstanceUsersList: (state, action) => {
      state.megaswitchInstanceUsersList = action.payload;
    },
    setMegaswitchInstanceUsersListFetchStatus: (state, action) => {
      state.megaswitchInstanceUsersListFetchStatus = action.payload;
    },
    setMegaswitchInstanceUserDetailsPostStatus: (state, action) => {
      state.megaswitchInstanceUserDetailsPostStatus = action.payload;
    },
    setMegaswitchInstanceUserDetailsDeleteStatus: (state, action) => {
      state.megaswitchInstanceUserDetailsDeleteStatus = action.payload;
    },
    setNewlyCreatedInstanceUser: (state, action) => {
      state.newlyCreatedInstanceUser = action.payload;
    },
    // Domains
    setMegaswitchDomainsList: (state, action) => {
      state.megaswitchDomainsList = action.payload;
    },
    setMegaswitchDomainsListFetchStatus: (state, action) => {
      state.megaswitchDomainsListFetchStatus = action.payload;
    },
    setMegaswitchDomainDetailsPostStatus: (state, action) => {
      state.megaswitchDomainDetailsPostStatus = action.payload;
    },
    setMegaswitchDomainDetailsUpdateStatus: (state, action) => {
      state.megaswitchDomainDetailsUpdateStatus = action.payload;
    },
    setMegaswitchDomainDetailsDeleteStatus: (state, action) => {
      state.megaswitchDomainDetailsDeleteStatus = action.payload;
    },
    // Carriers
    setMegaswitchCarriersList: (state, action) => {
      state.megaswitchCarriersList = action.payload;
    },
    setMegaswitchCarriersListFetchStatus: (state, action) => {
      state.megaswitchCarriersListFetchStatus = action.payload;
    },
    setMegaswitchCarrierDetailsPostStatus: (state, action) => {
      state.megaswitchCarrierDetailsPostStatus = action.payload;
    },
    setMegaswitchCarrierDetailsUpdateStatus: (state, action) => {
      state.megaswitchCarrierDetailsUpdateStatus = action.payload;
    },
    setMegaswitchCarrierDetailsDeleteStatus: (state, action) => {
      state.megaswitchCarrierDetailsDeleteStatus = action.payload;
    },
    // DIDs
    setMegaswitchDidsList: (state, action) => {
      state.megaswitchDidsList = action.payload;
    },
    setMegaswitchDidsListFetchStatus: (state, action) => {
      state.megaswitchDidsListFetchStatus = action.payload;
    },
    setMegaswitchDidDetailsPostStatus: (state, action) => {
      state.megaswitchDidDetailsPostStatus = action.payload;
    },
    setMegaswitchDidDetailsUpdateStatus: (state, action) => {
      state.megaswitchDidDetailsUpdateStatus = action.payload;
    },
    setMegaswitchDidDetailsDeleteStatus: (state, action) => {
      state.megaswitchDidDetailsDeleteStatus = action.payload;
    },
    // Users
    setMegaswitchUsersList: (state, action) => {
      state.megaswitchUsersList = action.payload;
    },
    setMegaswitchUsersListFetchStatus: (state, action) => {
      state.megaswitchUsersListFetchStatus = action.payload;
    },
    setMegaswitchUserDetailsPostStatus: (state, action) => {
      state.megaswitchUserDetailsPostStatus = action.payload;
    },
    setMegaswitchUserDetailsUpdateStatus: (state, action) => {
      state.megaswitchUserDetailsUpdateStatus = action.payload;
    },
    setMegaswitchUserDetailsDeleteStatus: (state, action) => {
      state.megaswitchUserDetailsDeleteStatus = action.payload;
    },
    // Outbound Routes
    setMegaswitchOutboundRoutesList: (state, action) => {
      state.megaswitchOutboundRoutesList = action.payload;
    },
    setMegaswitchOutboundRoutesListFetchStatus: (state, action) => {
      state.megaswitchOutboundRoutesListFetchStatus = action.payload;
    },
    setMegaswitchOutboundRouteDetailsPostStatus: (state, action) => {
      state.megaswitchOutboundRouteDetailsPostStatus = action.payload;
    },
    setMegaswitchOutboundRouteDetailsUpdateStatus: (state, action) => {
      state.megaswitchOutboundRouteDetailsUpdateStatus = action.payload;
    },
    setMegaswitchOutboundRouteDetailsDeleteStatus: (state, action) => {
      state.megaswitchOutboundRouteDetailsDeleteStatus = action.payload;
    },
    // Cores
    setMegaswitchCoresList: (state, action) => {
      state.megaswitchCoresList = action.payload;
    },
    setMegaswitchCoresListFetchStatus: (state, action) => {
      state.megaswitchCoresListFetchStatus = action.payload;
    },
    setMegaswitchCoreDetailsPostStatus: (state, action) => {
      state.megaswitchCoreDetailsPostStatus = action.payload;
    },
    setMegaswitchCoreDetailsUpdateStatus: (state, action) => {
      state.megaswitchCoreDetailsUpdateStatus = action.payload;
    },
    setMegaswitchCoreDetailsDeleteStatus: (state, action) => {
      state.megaswitchCoreDetailsDeleteStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'megaswitch') {
        state.megaswitchInstancesRouteParams = { ...state.megaswitchInstancesRouteParams, ...payload.data };
      }
      if (payload.reducer === 'megaswitch-users') {
        state.megaswitchInstanceUsersRouteParams = { ...state.megaswitchInstanceUsersRouteParams, ...payload.data };
      }
      if (payload.reducer === 'megaswitch-domains') {
        state.megaswitchDomainsRouteParams = { ...state.megaswitchDomainsRouteParams, ...payload.data };
      }
      if (payload.reducer === 'megaswitch-carriers') {
        state.megaswitchCarriersRouteParams = { ...state.megaswitchCarriersRouteParams, ...payload.data };
      }
      if (payload.reducer === 'megaswitch-dids') {
        state.megaswitchDidsRouteParams = { ...state.megaswitchDidsRouteParams, ...payload.data };
      }
      if (payload.reducer === 'megaswitch-users') {
        state.megaswitchUsersRouteParams = { ...state.megaswitchUsersRouteParams, ...payload.data };
      }
      if (payload.reducer === 'megaswitch-outbound-routes') {
        state.megaswitchOutboundRoutesRouteParams = { ...state.megaswitchOutboundRoutesRouteParams, ...payload.data };
      }
      if (payload.reducer === 'megaswitch-cores') {
        state.megaswitchCoresRouteParams = { ...state.megaswitchCoresRouteParams, ...payload.data };
      }
    });

    // GET MEGASWITCH INSTANCES LIST
    builder.addCase(getMegaswitchInstancesList.pending, state => {
      state.megaswitchInstancesListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getMegaswitchInstancesList.rejected, (state, action) => {
      if (action.payload) {
        state.megaswitchInstancesListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getMegaswitchInstancesList.fulfilled, (state, action) => {
      state.megaswitchInstancesListFetchStatus = FETCH_STATUS.FULFILLED;
      state.megaswitchInstancesList = action.payload;
    });

    // GET MEGASWITCH INSTANCE DETAILS
    builder.addCase(getMegaswitchInstanceDetails.pending, state => {
      state.megaswitchInstanceDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getMegaswitchInstanceDetails.rejected, (state, action) => {
      state.megaswitchInstanceDetailsFetchStatus = FETCH_STATUS.REJECTED;
      state.megaswitchInstanceDetails = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getMegaswitchInstanceDetails.fulfilled, (state, action) => {
      state.megaswitchInstanceDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.megaswitchInstanceDetails = action.payload;
    });

    // CREATE MEGASWITCH INSTANCE DETAILS
    builder.addCase(postMegaswitchInstanceDetails.pending, state => {
      state.megaswitchInstanceDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postMegaswitchInstanceDetails.rejected, (state, action) => {
      state.megaswitchInstanceDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postMegaswitchInstanceDetails.fulfilled, state => {
      state.megaswitchInstanceDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Megaswitch instance has been created!');
      });
    });

    // Update MEGASWITCH INSTANCE DETAILS
    builder.addCase(patchMegaswitchInstanceDetails.pending, state => {
      state.megaswitchInstanceDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchMegaswitchInstanceDetails.rejected, (state, action) => {
      state.megaswitchInstanceDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchMegaswitchInstanceDetails.fulfilled, state => {
      state.megaswitchInstanceDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Megaswitch instance has been created!');
      });
    });

    // DELETE MEGASWITCH INSTANCE DETAILS
    builder.addCase(deleteMegaswitchInstanceDetails.pending, state => {
      state.megaswitchInstanceDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteMegaswitchInstanceDetails.rejected, (state, action) => {
      state.megaswitchInstanceDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteMegaswitchInstanceDetails.fulfilled, state => {
      state.megaswitchInstanceDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Megaswitch instance has been archived!');
      });
    });

    // GET MEGASWITCH INSTANCE USERS LIST
    builder.addCase(getMegaswitchInstanceUsersList.pending, state => {
      state.megaswitchInstanceUsersListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getMegaswitchInstanceUsersList.rejected, (state, action) => {
      if (action.payload) {
        state.megaswitchInstanceUsersListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getMegaswitchInstanceUsersList.fulfilled, (state, action) => {
      state.megaswitchInstanceUsersListFetchStatus = FETCH_STATUS.FULFILLED;
      state.megaswitchInstanceUsersList = action.payload;
    });

    // CREATE MEGASWITCH INSTANCE USER DETAILS
    builder.addCase(postMegaswitchInstanceUserDetails.pending, state => {
      state.megaswitchInstanceUserDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postMegaswitchInstanceUserDetails.rejected, (state, action) => {
      state.megaswitchInstanceUserDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postMegaswitchInstanceUserDetails.fulfilled, (state, action) => {
      state.megaswitchInstanceUserDetailsPostStatus = FETCH_STATUS.FULFILLED;
      // Combine API response with form data
      state.newlyCreatedInstanceUser = {
        ...action.payload.response,
        name: action.payload.formData.name,
        description: action.payload.formData.description || '',
      };
      setTimeout(() => {
        snackbarUtils.success('Instance user has been created!');
      });
    });

    // DELETE MEGASWITCH INSTANCE USER DETAILS
    builder.addCase(deleteMegaswitchInstanceUserDetails.pending, state => {
      state.megaswitchInstanceUserDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteMegaswitchInstanceUserDetails.rejected, (state, action) => {
      state.megaswitchInstanceUserDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteMegaswitchInstanceUserDetails.fulfilled, state => {
      state.megaswitchInstanceUserDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Instance user has been archived!');
      });
    });

    // GET MEGASWITCH DOMAINS LIST
    builder.addCase(getMegaswitchDomainsList.pending, state => {
      state.megaswitchDomainsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getMegaswitchDomainsList.rejected, (state, action) => {
      if (action.payload) {
        state.megaswitchDomainsListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getMegaswitchDomainsList.fulfilled, (state, action) => {
      state.megaswitchDomainsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.megaswitchDomainsList = action.payload;
    });

    // CREATE MEGASWITCH DOMAIN DETAILS
    builder.addCase(postMegaswitchDomainDetails.pending, state => {
      state.megaswitchDomainDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postMegaswitchDomainDetails.rejected, (state, action) => {
      state.megaswitchDomainDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postMegaswitchDomainDetails.fulfilled, state => {
      state.megaswitchDomainDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Domain has been added!');
      });
    });

    // UPDATE MEGASWITCH DOMAIN DETAILS
    builder.addCase(updateMegaswitchDomainDetails.pending, state => {
      state.megaswitchDomainDetailsUpdateStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(updateMegaswitchDomainDetails.rejected, (state, action) => {
      state.megaswitchDomainDetailsUpdateStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(updateMegaswitchDomainDetails.fulfilled, state => {
      state.megaswitchDomainDetailsUpdateStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Domain has been updated!');
      });
    });

    // DELETE MEGASWITCH DOMAIN DETAILS
    builder.addCase(deleteMegaswitchDomainDetails.pending, state => {
      state.megaswitchDomainDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteMegaswitchDomainDetails.rejected, (state, action) => {
      state.megaswitchDomainDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteMegaswitchDomainDetails.fulfilled, state => {
      state.megaswitchDomainDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Domain has been deleted!');
      });
    });

    // GET MEGASWITCH CARRIERS LIST
    builder.addCase(getMegaswitchCarriersList.pending, state => {
      state.megaswitchCarriersListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getMegaswitchCarriersList.rejected, (state, action) => {
      if (action.payload) {
        state.megaswitchCarriersListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getMegaswitchCarriersList.fulfilled, (state, action) => {
      state.megaswitchCarriersListFetchStatus = FETCH_STATUS.FULFILLED;
      state.megaswitchCarriersList = action.payload;
    });

    // CREATE MEGASWITCH CARRIER DETAILS
    builder.addCase(postMegaswitchCarrierDetails.pending, state => {
      state.megaswitchCarrierDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postMegaswitchCarrierDetails.rejected, (state, action) => {
      state.megaswitchCarrierDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postMegaswitchCarrierDetails.fulfilled, state => {
      state.megaswitchCarrierDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Carrier has been added!');
      });
    });

    // UPDATE MEGASWITCH CARRIER DETAILS
    builder.addCase(updateMegaswitchCarrierDetails.pending, state => {
      state.megaswitchCarrierDetailsUpdateStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(updateMegaswitchCarrierDetails.rejected, (state, action) => {
      state.megaswitchCarrierDetailsUpdateStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(updateMegaswitchCarrierDetails.fulfilled, state => {
      state.megaswitchCarrierDetailsUpdateStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Carrier has been updated!');
      });
    });

    // DELETE MEGASWITCH CARRIER DETAILS
    builder.addCase(deleteMegaswitchCarrierDetails.pending, state => {
      state.megaswitchCarrierDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteMegaswitchCarrierDetails.rejected, (state, action) => {
      state.megaswitchCarrierDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteMegaswitchCarrierDetails.fulfilled, state => {
      state.megaswitchCarrierDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Carrier has been deleted!');
      });
    });

    // GET MEGASWITCH DIDS LIST
    builder.addCase(getMegaswitchDidsList.pending, state => {
      state.megaswitchDidsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getMegaswitchDidsList.rejected, (state, action) => {
      if (action.payload) {
        state.megaswitchDidsListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getMegaswitchDidsList.fulfilled, (state, action) => {
      state.megaswitchDidsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.megaswitchDidsList = action.payload;
    });

    // CREATE MEGASWITCH DID DETAILS
    builder.addCase(postMegaswitchDidDetails.pending, state => {
      state.megaswitchDidDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postMegaswitchDidDetails.rejected, (state, action) => {
      state.megaswitchDidDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postMegaswitchDidDetails.fulfilled, state => {
      state.megaswitchDidDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('DID has been added!');
      });
    });

    // UPDATE MEGASWITCH DID DETAILS
    builder.addCase(updateMegaswitchDidDetails.pending, state => {
      state.megaswitchDidDetailsUpdateStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(updateMegaswitchDidDetails.rejected, (state, action) => {
      state.megaswitchDidDetailsUpdateStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(updateMegaswitchDidDetails.fulfilled, state => {
      state.megaswitchDidDetailsUpdateStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('DID has been updated!');
      });
    });

    // DELETE MEGASWITCH DID DETAILS
    builder.addCase(deleteMegaswitchDidDetails.pending, state => {
      state.megaswitchDidDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteMegaswitchDidDetails.rejected, (state, action) => {
      state.megaswitchDidDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteMegaswitchDidDetails.fulfilled, state => {
      state.megaswitchDidDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('DID has been deleted!');
      });
    });

    // GET MEGASWITCH USERS LIST
    builder.addCase(getMegaswitchUsersList.pending, state => {
      state.megaswitchUsersListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getMegaswitchUsersList.rejected, (state, action) => {
      if (action.payload) {
        state.megaswitchUsersListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getMegaswitchUsersList.fulfilled, (state, action) => {
      state.megaswitchUsersListFetchStatus = FETCH_STATUS.FULFILLED;
      state.megaswitchUsersList = action.payload;
    });

    // POST MEGASWITCH USER DETAILS
    builder.addCase(postMegaswitchUserDetails.pending, state => {
      state.megaswitchUserDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postMegaswitchUserDetails.rejected, (state, action) => {
      state.megaswitchUserDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postMegaswitchUserDetails.fulfilled, state => {
      state.megaswitchUserDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('User has been created!');
      });
    });

    // UPDATE MEGASWITCH USER DETAILS
    builder.addCase(updateMegaswitchUserDetails.pending, state => {
      state.megaswitchUserDetailsUpdateStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(updateMegaswitchUserDetails.rejected, (state, action) => {
      state.megaswitchUserDetailsUpdateStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(updateMegaswitchUserDetails.fulfilled, state => {
      state.megaswitchUserDetailsUpdateStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('User has been updated!');
      });
    });

    // DELETE MEGASWITCH USER DETAILS
    builder.addCase(deleteMegaswitchUserDetails.pending, state => {
      state.megaswitchUserDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteMegaswitchUserDetails.rejected, (state, action) => {
      state.megaswitchUserDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteMegaswitchUserDetails.fulfilled, state => {
      state.megaswitchUserDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('User has been deleted!');
      });
    });

    // GET MEGASWITCH OUTBOUND ROUTES LIST
    builder.addCase(getMegaswitchOutboundRoutesList.pending, state => {
      state.megaswitchOutboundRoutesListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getMegaswitchOutboundRoutesList.rejected, (state, action) => {
      if (action.payload) {
        state.megaswitchOutboundRoutesListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getMegaswitchOutboundRoutesList.fulfilled, (state, action) => {
      state.megaswitchOutboundRoutesListFetchStatus = FETCH_STATUS.FULFILLED;
      state.megaswitchOutboundRoutesList = action.payload;
    });

    // CREATE MEGASWITCH OUTBOUND ROUTE DETAILS
    builder.addCase(postMegaswitchOutboundRouteDetails.pending, state => {
      state.megaswitchOutboundRouteDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postMegaswitchOutboundRouteDetails.rejected, (state, action) => {
      state.megaswitchOutboundRouteDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postMegaswitchOutboundRouteDetails.fulfilled, state => {
      state.megaswitchOutboundRouteDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Outbound route has been added!');
      });
    });

    // UPDATE MEGASWITCH OUTBOUND ROUTE DETAILS
    builder.addCase(updateMegaswitchOutboundRouteDetails.pending, state => {
      state.megaswitchOutboundRouteDetailsUpdateStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(updateMegaswitchOutboundRouteDetails.rejected, (state, action) => {
      state.megaswitchOutboundRouteDetailsUpdateStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(updateMegaswitchOutboundRouteDetails.fulfilled, state => {
      state.megaswitchOutboundRouteDetailsUpdateStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Outbound route has been updated!');
      });
    });

    // DELETE MEGASWITCH OUTBOUND ROUTE DETAILS
    builder.addCase(deleteMegaswitchOutboundRouteDetails.pending, state => {
      state.megaswitchOutboundRouteDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteMegaswitchOutboundRouteDetails.rejected, (state, action) => {
      state.megaswitchOutboundRouteDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteMegaswitchOutboundRouteDetails.fulfilled, state => {
      state.megaswitchOutboundRouteDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Outbound route has been deleted!');
      });
    });

    // GET MEGASWITCH CORES LIST
    builder.addCase(getMegaswitchCoresList.pending, state => {
      state.megaswitchCoresListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getMegaswitchCoresList.rejected, (state, action) => {
      if (action.payload) {
        state.megaswitchCoresListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getMegaswitchCoresList.fulfilled, (state, action) => {
      state.megaswitchCoresListFetchStatus = FETCH_STATUS.FULFILLED;
      state.megaswitchCoresList = action.payload;
    });

    // CREATE MEGASWITCH CORE DETAILS
    builder.addCase(postMegaswitchCoreDetails.pending, state => {
      state.megaswitchCoreDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postMegaswitchCoreDetails.rejected, (state, action) => {
      state.megaswitchCoreDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postMegaswitchCoreDetails.fulfilled, state => {
      state.megaswitchCoreDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Core has been added!');
      });
    });

    // UPDATE MEGASWITCH CORE DETAILS
    builder.addCase(updateMegaswitchCoreDetails.pending, state => {
      state.megaswitchCoreDetailsUpdateStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(updateMegaswitchCoreDetails.rejected, (state, action) => {
      state.megaswitchCoreDetailsUpdateStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(updateMegaswitchCoreDetails.fulfilled, state => {
      state.megaswitchCoreDetailsUpdateStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Core has been updated!');
      });
    });

    // DELETE MEGASWITCH CORE DETAILS
    builder.addCase(deleteMegaswitchCoreDetails.pending, state => {
      state.megaswitchCoreDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteMegaswitchCoreDetails.rejected, (state, action) => {
      state.megaswitchCoreDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteMegaswitchCoreDetails.fulfilled, state => {
      state.megaswitchCoreDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Core has been deleted!');
      });
    });
  },
});

export const {
  setMegaswitchInstancesList,
  setMegaswitchInstancesListFetchStatus,
  setMegaswitchInstanceDetailsPostStatus,
  setMegaswitchInstanceDetailsPatchStatus,
  setMegaswitchInstanceDetailsDeleteStatus,
  setMegaswitchInstanceUserDetailsPostStatus,
  setNewlyCreatedInstanceUser,
  setMegaswitchDomainsList,
  setMegaswitchDomainsListFetchStatus,
  setMegaswitchDomainDetailsPostStatus,
  setMegaswitchDomainDetailsUpdateStatus,
  setMegaswitchDomainDetailsDeleteStatus,
  setMegaswitchCarriersList,
  setMegaswitchCarriersListFetchStatus,
  setMegaswitchCarrierDetailsPostStatus,
  setMegaswitchCarrierDetailsUpdateStatus,
  setMegaswitchCarrierDetailsDeleteStatus,
  setMegaswitchDidsList,
  setMegaswitchDidsListFetchStatus,
  setMegaswitchDidDetailsPostStatus,
  setMegaswitchDidDetailsUpdateStatus,
  setMegaswitchDidDetailsDeleteStatus,
  setMegaswitchUsersList,
  setMegaswitchUsersListFetchStatus,
  setMegaswitchUserDetailsPostStatus,
  setMegaswitchUserDetailsUpdateStatus,
  setMegaswitchUserDetailsDeleteStatus,
  setMegaswitchOutboundRoutesList,
  setMegaswitchOutboundRoutesListFetchStatus,
  setMegaswitchOutboundRouteDetailsPostStatus,
  setMegaswitchOutboundRouteDetailsUpdateStatus,
  setMegaswitchOutboundRouteDetailsDeleteStatus,
  setMegaswitchCoresList,
  setMegaswitchCoresListFetchStatus,
  setMegaswitchCoreDetailsPostStatus,
  setMegaswitchCoreDetailsUpdateStatus,
  setMegaswitchCoreDetailsDeleteStatus,
} = megaswitchSlice.actions;

export default megaswitchSlice.reducer;
